import { createAction, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type AuthStateType, AuthState } from '../utils/constants';

import type { User } from 'oidc-client-ts';


export interface AuthSlice {
  entry: {
    authState: AuthStateType;
    expiresAt: number | undefined;
    email: string;
    isTokenSet: boolean;
  };
  status: 'idle' | 'loading' | 'failed' | 'succeeded';
  error: string | null;
}

export const initialState: AuthSlice = {
  entry: {
    authState: AuthState.initialization,
    expiresAt: undefined,
    email: '',
    isTokenSet: false
  },
  status: 'idle',
  error: null
};

const authSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    setStatusLoading(state) {
      state.status = 'loading';
      state.error = null;
    },

    setStatusSucceeded(state) {
      state.status = 'succeeded';
      state.error = null;
    },

    setStatusFailed(state, action: PayloadAction<string | null>) {
      state.status = 'failed';
      state.error = action.payload;
    },

    setAuthStateType(state, action: PayloadAction<AuthStateType>) {
      state.entry.authState = action.payload;
    },

    setExpiresAt(state, action: PayloadAction<AuthSlice['entry']['expiresAt']>) {
      state.entry.expiresAt = action.payload;
    },

    setEmail(state, action: PayloadAction<AuthSlice['entry']['email']>) {
      state.entry.email = action.payload;
    },

    setIsTokenSet(state, action: PayloadAction<AuthSlice['entry']['isTokenSet']>) {
      state.entry.isTokenSet = action.payload;
    },

    updateToken(state, action: PayloadAction<{ user: User | null; role: AuthStateType }>) {
      const { user, role } = action.payload;
      if (user && role) {
        state.entry.authState = role;
        state.entry.expiresAt = user.expires_at;
        state.entry.email = user.profile?.preferred_username ?? '';
        state.entry.isTokenSet = true;
      }
    },

    clearAuth(state) {
      state.entry = {
        ...initialState.entry,
        authState: AuthState.guest
      };
    }
  }
});

export const signIn = createAction('authentication/signIn');
export const signOut = createAction('authentication/signOut');
export const updateState = createAction('authentication/updateState');
export const updateAuthState = createAction('authentication/updateAuthState');

export const {
  setStatusLoading,
  setStatusSucceeded,
  setStatusFailed,
  setAuthStateType,
  setExpiresAt,
  setEmail,
  setIsTokenSet,
  updateToken,
  clearAuth
} = authSlice.actions;
export default authSlice.reducer;
